/**
 * FigJam-style text note placement: toggle mode, ghost under cursor, click to commit
 * `textNote` node. New notes are added with `selected: true` so they open in edit mode.
 *
 * Ghost is seeded immediately from the last known cursor position on mode entry and
 * then tracked via a window-level mousemove (same pattern as `useDocPlacement`).
 */

import { useUserInfo } from "@/api/user";
import { NOTE_DEFAULT_SIZE } from "@cohesive/ui-components";
import { useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CANVAS_SNAP, snapToGrid } from "../canvas-controls.config";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { DEFAULT_NOTE_COLOR } from "../nodes/shared/CanvasColorPalettes";
import { attachNodeToSectionIfApplicable } from "../utils/sectionMembership";

// The ruled square sticky — single source is the chrome kit's contract.
export const NOTE_NODE_WIDTH = NOTE_DEFAULT_SIZE;
export const NOTE_NODE_HEIGHT = NOTE_DEFAULT_SIZE;

interface Position {
  x: number;
  y: number;
}

export interface UseTextNotePlacementParams {
  /** Last known cursor position over the canvas wrapper (screen coords). */
  lastCursorScreenPosRef: React.RefObject<Position | null>;
}

/**
 * Hook for managing text note placement mode - FigJam-style ghost preview and click to place
 */
export function useTextNotePlacement({
  lastCursorScreenPosRef,
}: UseTextNotePlacementParams) {
  const reactFlowInstance = useReactFlow();
  const { screenToFlowPosition } = reactFlowInstance;
  const { reactFlowWrapperRef } = useCanvasUi();
  const { addNode } = useCanvas();
  const { data: userInfo } = useUserInfo();
  const [isPlacingTextNote, setIsPlacingTextNote] = useState(false);
  const [ghostPosition, setGhostPosition] = useState<Position | null>(null);

  const placementFromScreenPos = useCallback(
    (screenPos: Position): Position => {
      const position = screenToFlowPosition(screenPos);
      const [gridX, gridY] = CANVAS_SNAP.grid;
      return {
        x: snapToGrid(position.x - NOTE_NODE_WIDTH / 2, gridX),
        y: snapToGrid(position.y - NOTE_NODE_HEIGHT / 2, gridY),
      };
    },
    [screenToFlowPosition],
  );

  const seedGhostFromScreenPos = useCallback(
    (screenPos: Position | null | undefined) => {
      if (!screenPos) {
        setGhostPosition(null);
        return;
      }
      lastCursorScreenPosRef.current = screenPos;
      setGhostPosition(placementFromScreenPos(screenPos));
    },
    [lastCursorScreenPosRef, placementFromScreenPos],
  );

  /** Always enter note placement (toolbar / flyout). Does not toggle off. */
  const startTextNotePlacement = useCallback(
    (screenPos?: Position | null) => {
      setIsPlacingTextNote(true);
      seedGhostFromScreenPos(screenPos ?? lastCursorScreenPosRef.current);
    },
    [lastCursorScreenPosRef, seedGhostFromScreenPos],
  );

  const handleToggleTextNoteMode = useCallback(() => {
    setIsPlacingTextNote((prev) => {
      const next = !prev;
      const cursor = lastCursorScreenPosRef.current;
      setGhostPosition(next && cursor ? placementFromScreenPos(cursor) : null);
      return next;
    });
  }, [lastCursorScreenPosRef, placementFromScreenPos]);

  // Escape key to cancel placement
  useEffect(() => {
    if (!isPlacingTextNote) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsPlacingTextNote(false);
        setGhostPosition(null);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isPlacingTextNote]);

  // Track the cursor at the window level while placing so the ghost follows
  // from the very first move — over the toolbar, nodes, and pane alike.
  useEffect(() => {
    if (!isPlacingTextNote) return;

    const handler = (e: MouseEvent) => {
      const wrapper = reactFlowWrapperRef.current;
      if (wrapper && !wrapper.contains(e.target as globalThis.Node)) return;
      const screenPos = { x: e.clientX, y: e.clientY };
      lastCursorScreenPosRef.current = screenPos;
      setGhostPosition(placementFromScreenPos(screenPos));
    };

    window.addEventListener("mousemove", handler);

    const cursor = lastCursorScreenPosRef.current;
    if (cursor) {
      setGhostPosition(placementFromScreenPos(cursor));
    }

    return () => window.removeEventListener("mousemove", handler);
  }, [
    isPlacingTextNote,
    reactFlowWrapperRef,
    lastCursorScreenPosRef,
    placementFromScreenPos,
  ]);

  // Place note on click (FigJam-style)
  const handlePaneClick = useCallback(
    (event: React.MouseEvent) => {
      if (!isPlacingTextNote) return;

      addNode({
        ...attachNodeToSectionIfApplicable({
          node: {
            id: uuidv4(),
            type: "textNote",
            position: placementFromScreenPos({
              x: event.clientX,
              y: event.clientY,
            }),
            width: NOTE_NODE_WIDTH,
            height: NOTE_NODE_HEIGHT,
            data: {
              title: "Note",
              content: "",
              noteColor: DEFAULT_NOTE_COLOR,
              // Authorship for the chrome foot — set once at create.
              author_name: userInfo?.user?.name ?? undefined,
              author_kind: "user",
              created_at: new Date().toISOString(),
            },
          },
          nodes: reactFlowInstance.getNodes(),
          getInternalNode: (id) => reactFlowInstance.getInternalNode(id),
        }),
        selected: true,
      });
      setIsPlacingTextNote(false);
      setGhostPosition(null);
    },
    [
      isPlacingTextNote,
      placementFromScreenPos,
      reactFlowInstance,
      addNode,
      userInfo?.user?.name,
    ],
  );

  return {
    isPlacingTextNote,
    setIsPlacingTextNote,
    ghostPosition,
    startTextNotePlacement,
    handleToggleTextNoteMode,
    handlePaneClick,
  };
}
